/**
 * 课堂签到。
 *
 * 三块：学员端的签到卡、主持人端的进度条、结束后的结果面板。
 *
 * 签到卡不做成弹窗遮罩：老师往往是一边讲一边发起签到的，
 * 遮罩会把正在讲的板书盖住，学员为了看清内容反而先去点关闭。
 * 做成贴在视频区上方的一条，不挡内容也躲不掉。
 */

import { useEffect, useState } from 'react'

import { hasPerm, Perm } from '../lib/protocol'
import { useRoomStore } from '../stores/roomStore'

/** 学员端：一条签到提示 + 一个按钮。 */
export function AttendCard() {
  const attend = useRoomStore((s) => s.attend)
  const marked = useRoomStore((s) => s.attendMarked)
  const markAttend = useRoomStore((s) => s.markAttend)
  const selfId = useRoomStore((s) => s.selfId)
  const members = useRoomStore((s) => s.members)
  const me = members.get(selfId)

  // 剩余秒数。每秒重算一次而不是自减：标签页被切到后台时
  // setInterval 会被节流，自减会越走越慢，最后停在一个不对的数上。
  const [left, setLeft] = useState(0)
  useEffect(() => {
    if (!attend?.limitS) return setLeft(0)
    const endAt = attend.time * 1000 + attend.limitS * 1000
    const tick = () => setLeft(Math.max(0, Math.ceil((endAt - Date.now()) / 1000)))
    tick()
    const t = setInterval(tick, 500)
    return () => clearInterval(t)
  }, [attend])

  // 主持人不用给自己签到，他看的是进度那一块（AttendProgress）。
  if (!attend || (me && hasPerm(me.prop, Perm.Op))) return null

  return (
    <div className={`attend-card${marked ? ' done' : ''}`}>
      <span className="attend-title">
        {marked ? '已签到' : '课堂签到'}
        {!!attend.limitS && !marked && <em className="attend-left">剩 {left} 秒</em>}
      </span>
      {marked ? (
        <span className="attend-ok">✓ 老师已收到</span>
      ) : (
        <button className="primary" onClick={markAttend}>
          签到
        </button>
      )}
    </div>
  )
}

/** 主持人端：进度条 + 随时结束。 */
export function AttendProgress() {
  const attend = useRoomStore((s) => s.attend)
  const stat = useRoomStore((s) => s.attendStat)
  const endAttend = useRoomStore((s) => s.endAttend)
  const selfId = useRoomStore((s) => s.selfId)
  const me = useRoomStore((s) => s.members).get(selfId)

  if (!attend || !me || !hasPerm(me.prop, Perm.Op)) return null
  const marked = stat?.marked ?? 0
  const total = stat?.total ?? attend.expected
  const pct = total > 0 ? Math.round((marked / total) * 100) : 0

  return (
    <div className="attend-card host">
      <span className="attend-title">
        签到中 <em>{marked} / {total}</em>
      </span>
      <div className="attend-bar">
        <div className="attend-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <button onClick={endAttend}>结束签到</button>
    </div>
  )
}

/**
 * 结束后的结果面板。
 *
 * 缺席名单排在签到名单前面：老师点开这个面板要找的是「谁没来」，
 * 把它压在一长串已签到的人下面等于让人自己去翻。
 */
export function AttendResult() {
  const result = useRoomStore((s) => s.attendResult)
  const clear = useRoomStore((s) => s.clearAttendResult)
  const selfId = useRoomStore((s) => s.selfId)
  const me = useRoomStore((s) => s.members).get(selfId)

  if (!result) return null
  const isHost = !!me && hasPerm(me.prop, Perm.Op)
  const rate = result.total > 0 ? Math.round((result.marked.length / result.total) * 100) : 0

  // 学员那边只告诉他自己签上没有，不公布谁缺席 ——
  // 当众点名是另一回事，不该由一个签到功能顺带做了。
  if (!isHost) {
    const mine = result.marked.some((m) => m.userId === selfId)
    return (
      <div className="dlg-mask" onClick={clear}>
        <div className="dlg narrow" onClick={(e) => e.stopPropagation()}>
          <h3>签到结束</h3>
          <p className={mine ? 'attend-ok' : 'login-err'}>
            {mine ? '你已签到' : '你没有签到，如有情况请联系老师'}
          </p>
          <div className="dlg-foot">
            <button className="primary" onClick={clear}>知道了</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="dlg-mask" onClick={clear}>
      <div className="dlg" onClick={(e) => e.stopPropagation()}>
        <h3>
          签到结果 <em className="attend-rate">{rate}%</em>
        </h3>
        <p className="adm-hint">
          应到 {result.total} 人，实到 {result.marked.length} 人。
          记录已保存，可在管理后台按教室查历史。
        </p>

        {result.missing.length > 0 && (
          <div className="attend-list">
            <div className="attend-list-head warn">未签到（{result.missing.length}）</div>
            <ul>
              {result.missing.map((m) => (
                <li key={m.userId}>{m.nick || m.account}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="attend-list">
          <div className="attend-list-head">已签到（{result.marked.length}）</div>
          <ul>
            {result.marked.map((m) => (
              <li key={m.userId}>
                {m.nick || m.account}
                {m.status === 'late' && <em className="tag tag-away">迟到</em>}
              </li>
            ))}
          </ul>
        </div>

        <div className="dlg-foot">
          <button className="primary" onClick={clear}>关闭</button>
        </div>
      </div>
    </div>
  )
}

/** 发起签到的对话框：只需要选一个限时。 */
export function AttendStartDialog({ onClose }: { onClose: () => void }) {
  const startAttend = useRoomStore((s) => s.startAttend)
  // 默认 60 秒。太短的话网络慢的学员来不及点，
  // 太长又会把课拖住 —— 一分钟是课堂上点一下的合理上限。
  const [limitS, setLimitS] = useState(60)

  const go = () => {
    startAttend(limitS)
    onClose()
  }

  return (
    <div className="dlg-mask" onClick={onClose}>
      <div className="dlg narrow" onClick={(e) => e.stopPropagation()}>
        <h3>发起签到</h3>
        <p className="adm-hint">
          当前在场的学员会收到签到提示。记录会保存下来，可在管理后台按教室查。
        </p>
        <label>
          限时
          <select value={limitS} onChange={(e) => setLimitS(Number(e.target.value))}>
            <option value={30}>30 秒</option>
            <option value={60}>1 分钟</option>
            <option value={120}>2 分钟</option>
            <option value={300}>5 分钟</option>
            <option value={0}>不限时（手动结束）</option>
          </select>
        </label>
        <p className="adm-hint">
          {limitS > 0
            ? '到时自动结束；超时后仍可补签，会记为「迟到」。'
            : '不会自动结束，需要你手动点「结束签到」。'}
        </p>
        <div className="dlg-foot">
          <button onClick={onClose}>取消</button>
          <button className="primary" onClick={go}>开始</button>
        </div>
      </div>
    </div>
  )
}
